import { useEffect, useMemo, useState } from "react";
import { BASE_URL } from "../../const";
import Navbar from "../../components/Navbar";

const STAGES = [
  { key: "sku_creation", label: "SKU Creation" },
  { key: "unit_economics", label: "Unit Economics" },
  { key: "packaging", label: "Packaging" },
  { key: "inbound", label: "Inbound" },
  { key: "catalogue", label: "Catalogue" },
  { key: "video_created", label: "Video" },
  { key: "initial_mp_live", label: "Initial MP Live" },
  { key: "other_mp_live", label: "Other MP Live" },
];

const STAGE_STATUS_OPTIONS = ["not_started", "in_progress", "completed", "na"];
const NPD_STATUS_OPTIONS = ["pending", "in_progress", "completed", "rejected"];
const GST_PERCENT_OPTIONS = ["0", "5", "18"];
const OUTER_STATUS_COLUMNS = STAGES.map((s) => ({
  key: `${s.key}_status`,
  label: s.label,
}));

const BUNDLE_FIXED = [
  { key: "product_name", label: "Product Name", type: "text" },
  { key: "sku", label: "SKU", type: "text" },
  { key: "gst_percent", label: "GST %", type: "number" },
  { key: "hsn", label: "HSN", type: "text" },
  { key: "unit_cost", label: "Unit Cost", type: "number" },
  { key: "quantity", label: "Quantity", type: "number" },
];

const VARIATION_FIXED = [
  { key: "name_type", label: "Name / Type", type: "text" },
  { key: "dimensions", label: "Dimensions(inch)", type: "text" },
  { key: "dead_weight", label: "Dead Weight(gms)", type: "number" },
  { key: "volumetric_weight", label: "Vol. Weight(gms)", type: "number" },
  { key: "mrp", label: "MRP", type: "number" },
];

const BASIC_FIELDS = [
  "date_of_ideation", "product_name", "i_sku", "i_sku_type", "about_product",
  "usp", "key_highlight_points", "application", "product_category", "target_cost",
  "target_cost_breakup", "expected_sp", "icd_ideation_closure_date", 
  "hsn", "gst_percent", "bundle_data", "variation_data", "primary_channel",
];

const ALL_FIELDS = [
  "date_of_ideation", "product_name", "i_sku", "i_sku_type", "about_product",
  "key_highlight_points", "product_category", "target_cost", "expected_sp",
  "icd_ideation_closure_date", "catalogue_link", "hsn", "gst_percent", "bundle_data",
  "variation_data", "primary_channel", "other_channels", "sku_creation_target_date",
  "unit_economics_target_date", "packaging_target_date", "inbound_target_date",
  "catalogue_target_date", "video_created_target_date", "initial_mp_live_target_date",
];

const EMPTY = {
  date_of_ideation: "",
  product_name: "",
  i_sku: "",
  i_sku_type: "single",
  about_product: "",
  usp: "",
  key_highlight_points: "",
  application: "",
  product_category: "",
  target_cost: "",
  target_cost_breakup: "",
  expected_sp: "",
  icd_ideation_closure_date: "",
  catalogue_link: "",
  technical_specifications: "",
  hsn: "",
  gst_percent: "",
  bundle_data: { columns: BUNDLE_FIXED.map((c) => c.key), rows: [] },
  variation_data: { columns: VARIATION_FIXED.map((c) => c.key), rows: [] },
  primary_channel: "",
  other_channels: "",
  final_npd_status: "pending",
  manual_tag: "",
  remarks: "",
  basic_details_filled_date: "",
  all_details_filled_date: "",
  ...Object.fromEntries(STAGES.flatMap((s) => [
    [`${s.key}_status`, "not_started"],
    [`${s.key}_target_date`, ""],
    [`${s.key}_completion_date`, ""],
  ])),
};

const API_FIELDS = [
  "date_of_ideation",
  "product_name",
  "i_sku",
  "i_sku_type",
  "about_product",
  "usp",
  "key_highlight_points",
  "application",
  "product_category",
  "target_cost",
  "target_cost_breakup",
  "expected_sp",
  "icd_ideation_closure_date",
  "catalogue_link",
  "technical_specifications",
  "hsn",
  "gst_percent",
  "bundle_data",
  "variation_data",
  "primary_channel",
  "other_channels",
  "sku_creation_status",
  "sku_creation_target_date",
  "sku_creation_completion_date",
  "unit_economics_status",
  "unit_economics_target_date",
  "unit_economics_completion_date",
  "packaging_status",
  "packaging_target_date",
  "packaging_completion_date",
  "inbound_status",
  "inbound_target_date",
  "inbound_completion_date",
  "catalogue_status",
  "catalogue_target_date",
  "catalogue_completion_date",
  "video_created_status",
  "video_created_target_date",
  "video_created_completion_date",
  "initial_mp_live_status",
  "initial_mp_live_target_date",
  "initial_mp_live_completion_date",
  "other_mp_live_status",
  "other_mp_live_target_date",
  "other_mp_live_completion_date",
  "final_npd_status",
  "manual_tag",
  "remarks",
  "basic_details_filled_date",
  "all_details_filled_date",
];

function isoToday() { return new Date().toISOString().slice(0, 10); }
function parseList(data) { return Array.isArray(data?.data) ? data.data : Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : []; }
function parseDropdownOptions(data) {
  return parseList(data)
    .map((row) => ({
      v: String(row?.value ?? "").trim(),
      l: String(row?.label ?? row?.value ?? "").trim(),
    }))
    .filter((row) => row.v);
}
function splitChannelValues(value) {
  if (Array.isArray(value)) {
    return [...new Set(value.map((x) => String(x ?? "").trim()).filter(Boolean))];
  }
  if (!value) return [];
  return [...new Set(
    String(value)
      .split(/[,|\n]/)
      .map((x) => x.trim())
      .filter(Boolean),
  )];
}
function joinChannelValues(values) {
  const list = splitChannelValues(values);
  return list.join(", ");
}

function normalizeGstPercentValue(value) {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  if (Number.isFinite(n) && [0, 5, 18].includes(n)) return String(n);
  return String(value);
}

function formatDateDDMMYY(date = new Date()) {
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yy = String(date.getFullYear()).slice(-2);

  const hh = String(date.getHours()).padStart(2, "0");
  const min = String(date.getMinutes()).padStart(2, "0");

  return `${dd}${mm}${yy}${hh}${min}`;
}

function generateISku(categoryId, categories) {
  const c = categories.find((x) => Number(x.id) === Number(categoryId));
  if (!c) return "";
  return `NPD-${formatDateDDMMYY()}-${(c.short_form || "GEN").toUpperCase()}`;
}

function initTable(raw, fixed) {
  const fixedKeys = fixed.map((c) => c.key);
  if (!raw) return { columns: fixedKeys, rows: [] };
  if (raw.remark) return { columns: fixedKeys, rows: [], remark: raw.remark };
  const rows = Array.isArray(raw.rows) ? raw.rows : Array.isArray(raw) ? raw : [];
  const cols = Array.isArray(raw.columns) ? raw.columns : [];
  const columns = [...fixedKeys, ...cols.filter((k) => !fixedKeys.includes(k))];
  return { columns, rows: rows.map((r) => Object.fromEntries(columns.map((k) => [k, r?.[k] ?? ""]))) };
}

function hasTableData(tbl) {
  if (!tbl) return false;
  if (tbl.remark) return true;
  return Array.isArray(tbl.rows) && tbl.rows.some((r) => Object.values(r || {}).some((v) => String(v ?? "").trim() !== ""));
}

function filled(v) {
  if (v === null || v === undefined) return false;
  if (typeof v === "string") return v.trim() !== "";
  if (Array.isArray(v)) return v.some((item) => String(item ?? "").trim() !== "");
  if (typeof v === "object") return hasTableData(v);
  return true;
}

function allFilled(form, fields) { return fields.every((f) => filled(form[f])); }

function normalizeDates(payload) {
  const out = { ...payload };
  Object.keys(out).forEach((k) => { if (k.includes("date") && out[k] === "") out[k] = null; });
  return out;
}

function getApiErrorMessage(json, fallback) {
  const baseMessage = String(json?.message || "").trim();
  const errors = json?.errors;

  if (errors && typeof errors === "object") {
    const firstErrorEntry = Object.entries(errors).find(([, value]) => {
      if (Array.isArray(value)) return value.length > 0;
      return value !== null && value !== undefined && String(value).trim() !== "";
    });

    if (firstErrorEntry) {
      const [field, value] = firstErrorEntry;
      const detail = Array.isArray(value) ? String(value[0] ?? "").trim() : String(value ?? "").trim();
      if (detail) return `${field}: ${detail}`;
    }
  }

  return baseMessage || fallback;
}

function pickApiFields(payload) {
  return API_FIELDS.reduce((acc, key) => {
    if (Object.prototype.hasOwnProperty.call(payload, key)) acc[key] = payload[key];
    return acc;
  }, {});
}

function toLabel(value) {
  return String(value || "").replaceAll("_", " ");
}

function formatDateCell(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString();
}

function statusColor(value) {
  const v = String(value || "").toLowerCase();
  if (v === "completed" || v === "live") return { bg: "#dcfce7", color: "#166534" };
  if (v === "in_progress" || v === "development" || v === "sku_created") return { bg: "#dbeafe", color: "#1d4ed8" };
  if (v === "not_started" || v === "pending" || v === "ideation") return { bg: "#fef3c7", color: "#92400e" };
  if (v === "rejected" || v === "cancelled") return { bg: "#fee2e2", color: "#b91c1c" };
  if (v === "na") return { bg: "#e5e7eb", color: "#4b5563" };
  return { bg: "#f3f4f6", color: "#374151" };
}

function StatusPill({ value }) {
  const t = statusColor(value);
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 9px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        background: t.bg,
        color: t.color,
        textTransform: "capitalize",
        whiteSpace: "nowrap",
      }}
    >
      {toLabel(value || "—")}
    </span>
  );
}

export default function NPDMaster() {
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [marketplaceOptions, setMarketplaceOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [refreshingData, setRefreshingData] = useState(false);
  const [refreshResult, setRefreshResult] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("new");
  const [selected, setSelected] = useState(null);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [summaryError, setSummaryError] = useState("");
  const [summaryISku, setSummaryISku] = useState("");
  const npdUrl = `${BASE_URL}/npd/`;
  const categoryUrl = `${BASE_URL}/categories/`;

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true); setError("");
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: token ? `Token ${token}` : "" };
      const marketplaceUrl = `${BASE_URL}/dropdowns/options/?field_name=marketplace`;
      const [n, c, m] = await Promise.all([fetch(npdUrl, { headers }), fetch(categoryUrl, { headers }), fetch(marketplaceUrl, { headers })]);
      const nj = await n.json(); const cj = await c.json();
      const mj = await m.json();
      if (!n.ok) throw new Error(nj?.message || "Failed to load NPD");
      if (!c.ok) throw new Error(cj?.message || "Failed to load categories");
      if (!m.ok) throw new Error(mj?.message || "Failed to load marketplace dropdown");
      setRows(parseList(nj)); setCategories(parseList(cj)); setMarketplaceOptions(parseDropdownOptions(mj));
    } catch (e) { setError(e.message || "Load failed"); } finally { setLoading(false); }
  }

  async function runDataRefresher() {
    setRefreshingData(true);
    setError("");
    setRefreshResult(null);
    try {
      const token = localStorage.getItem("token");
      const headers = { "Content-Type": "application/json", Authorization: token ? `Token ${token}` : "" };
      const res = await fetch(`${BASE_URL}/data-refresher/`, {
        method: "POST",
        headers,
        body: JSON.stringify({}),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || "Failed to refresh data");
      const payload = json?.data || json;
      setRefreshResult(payload);
      await load();
    } catch (e) {
      setError(e.message || "Failed to refresh data");
    } finally {
      setRefreshingData(false);
    }
  }

  const categoryOptions = useMemo(() => categories.map((c) => ({ id: c.id, label: c.category, short_form: c.short_form })), [categories]);
  const filtered = useMemo(() => rows.filter((r) => {
    const q = search.toLowerCase();
    const mq = !q || String(r.i_sku || "").toLowerCase().includes(q) || String(r.product_name || "").toLowerCase().includes(q);
    const ms = !statusFilter || r.final_npd_status === statusFilter;
    return mq && ms;
  }), [rows, search, statusFilter]);

  function openNew() { setMode("new"); setSelected({ ...EMPTY, date_of_ideation: isoToday() }); setOpen(true); }
  function openEdit(r) { setMode("edit"); setSelected(r); setOpen(true); }
  function closeModal() { setOpen(false); setSelected(null); }
  function closeSummaryModal() {
    setSummaryOpen(false);
    setSummaryLoading(false);
    setSummaryError("");
    setSummaryData(null);
    setSummaryISku("");
  }

  async function openStatusSummary(row) {
    const iSku = row?.i_sku;
    if (!iSku) return;

    setSummaryOpen(true);
    setSummaryLoading(true);
    setSummaryError("");
    setSummaryData(null);
    setSummaryISku(iSku);

    try {
      const token = localStorage.getItem("token");
      const authHeaders = { Authorization: token ? `Token ${token}` : "" };
      const syncUrl = `${BASE_URL}/npd/${encodeURIComponent(iSku)}/data-flow-sync/`;

      const syncRes = await fetch(syncUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify({}),
      });
      const syncJson = await syncRes.json();
      if (!syncRes.ok) throw new Error(syncJson?.message || "Failed to sync status summary");

      const res = await fetch(syncUrl, { headers: authHeaders });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || "Failed to load status summary");
      setSummaryData(json?.data || json);
      await load();
    } catch (e) {
      setSummaryError(e.message || "Failed to load status summary");
    } finally {
      setSummaryLoading(false);
    }
  }

  async function save(payload) {
    const token = localStorage.getItem("token");
    const headers = { "Content-Type": "application/json", Authorization: token ? `Token ${token}` : "" };
    try {
      const body = JSON.stringify(normalizeDates(payload));
      if (mode === "edit") {
        const res = await fetch(`${npdUrl}${payload.i_sku}/`, { method: "PUT", headers, body });
        const json = await res.json();
        if (!res.ok) throw new Error(getApiErrorMessage(json, "Update failed"));
        const data = json?.data || json;
        setRows((prev) => prev.map((r) => (r.i_sku === data.i_sku ? data : r)));
      } else {
        const res = await fetch(`${npdUrl}create/`, { method: "POST", headers, body });
        const json = await res.json();
        if (!res.ok) throw new Error(getApiErrorMessage(json, "Create failed"));
        const data = json?.data || json;
        setRows((prev) => [data, ...prev]);
      }
      closeModal();
    } catch (e) { alert(e.message || "Save failed"); }
  }

  return (
    <div>
      <Navbar />
      <div style={s.page}>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={s.header}>
          <div><h2 style={s.title}>NPD Master</h2><p style={s.sub}>Centered row-wise editor based on current model</p></div>
          <div style={{ display: "flex", gap: 8 }}>
            <button style={s.btnGhost} onClick={runDataRefresher} disabled={refreshingData}>
              {refreshingData ? "Refreshing..." : "Refresh Data"}
            </button>
            <button style={s.btn} onClick={openNew} disabled={refreshingData}>+ New NPD</button>
          </div>
        </div>
        {error && <div style={s.err}>{error}</div>}
        {refreshResult && (
          <div style={s.refreshInfo}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{refreshResult.message || "Data refresher completed"}</div>
            <div style={s.refreshGrid}>
              <span>Matched SKUs: {refreshResult?.counts?.matched_skus ?? 0}</span>
              <span>UE created: {refreshResult?.counts?.unit_economics_created ?? 0}</span>
              <span>Listing created: {refreshResult?.counts?.listing_created ?? 0}</span>
              <span>Catalogue created: {refreshResult?.counts?.catalogue_created ?? 0}</span>
            </div>
          </div>
        )}
        <div style={s.filters}>
          <input style={s.input} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search I-SKU / product" />
          <select style={s.input} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {NPD_STATUS_OPTIONS.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <div style={s.list}>
          <div style={s.tblScroll}>
            <table style={s.outerTable}>
              <thead>
                <tr>
                  <th style={s.outerTh}>Ideation Date</th>
                  <th style={s.outerTh}>ICD</th>
                  <th style={s.outerTh}>I-SKU</th>
                  <th style={s.outerTh}>Product</th>
                  <th style={s.outerTh}>Category</th>
                  {OUTER_STATUS_COLUMNS.map((col) => (
                    <th key={col.key} style={s.outerTh}>{col.label}</th>
                  ))}
                  <th style={s.outerTh}>Final Status</th>
                  <th style={s.outerTh}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td style={s.outerTdEmpty} colSpan={OUTER_STATUS_COLUMNS.length + 7}>Loading...</td>
                  </tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr>
                    <td style={s.outerTdEmpty} colSpan={OUTER_STATUS_COLUMNS.length + 7}>No rows found</td>
                  </tr>
                )}
                {!loading && filtered.map((r) => (
                  <tr
                    key={r.i_sku}
                    style={s.outerTr}
                    onClick={() => openEdit(r)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openEdit(r);
                      }
                    }}
                  >
                    <td style={s.outerTd}>{formatDateCell(r.date_of_ideation)}</td>
                    <td style={s.outerTd}>{formatDateCell(r.icd_ideation_closure_date)}</td>
                    <td style={s.outerTd}>{r.i_sku}</td>
                    <td style={s.outerTd}>{r.product_name}</td>
                    <td style={s.outerTd}>{r.product_category_detail?.category || r.product_category || ""}</td>
                    {OUTER_STATUS_COLUMNS.map((col) => (
                      <td key={col.key} style={s.outerTd}>
                        <StatusPill value={r[col.key]} />
                      </td>
                    ))}
                    <td style={s.outerTd}><StatusPill value={r.final_npd_status} /></td>
                    <td style={s.outerTd}>
                      <div style={s.actionRow}>
                        <button
                          type="button"
                          style={s.btnGhost}
                          onClick={(e) => {
                            e.stopPropagation();
                            openStatusSummary(r);
                          }}
                        >
                          Status Summary
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {open && selected && (
          <>
            <div style={s.overlay} onClick={closeModal} />
            <NpdModal
              mode={mode}
              data={selected}
              categories={categoryOptions}
              marketplaceOptions={marketplaceOptions}
              onClose={closeModal}
              onSave={save}
            />
          </>
        )}
        {summaryOpen && (
          <>
            <div style={s.overlay} onClick={closeSummaryModal} />
            <StatusSummaryModal
              iSku={summaryISku}
              loading={summaryLoading}
              error={summaryError}
              data={summaryData}
              onClose={closeSummaryModal}
            />
          </>
        )}
      </div>
    </div>
  );
}

function StatusSummaryModal({ iSku, loading, error, data, onClose }) {
  return (
    <div style={s.summaryModal}>
      <div style={s.modalHead}>
        <div>
          <strong>Status Summary</strong>
          <div style={s.small}>{iSku || "-"}</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={s.btnClose} onClick={onClose}>x</button>
        </div>
      </div>
      <div style={s.summaryBody}>
        {loading && (
          <div style={s.summaryLoading}>
            <div style={s.loaderDot} />
            <span>Loading status summary...</span>
          </div>
        )}
        {!loading && error && <div style={s.err}>{error}</div>}
        {!loading && !error && (
          <pre style={s.summaryPre}>
            {JSON.stringify(data || {}, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}

function NpdModal({ mode, data, categories, marketplaceOptions, onClose, onSave }) {
  const [form, setForm] = useState({ ...EMPTY, ...data });
  const [otherChannelsOpen, setOtherChannelsOpen] = useState(false);
  useEffect(() => {
    setForm({
      ...EMPTY, ...data,
      gst_percent: normalizeGstPercentValue(data?.gst_percent),
      bundle_data: initTable(data?.bundle_data, BUNDLE_FIXED),
      variation_data: initTable(data?.variation_data, VARIATION_FIXED),
      primary_channel: String(data?.primary_channel || ""),
      other_channels: splitChannelValues(data?.other_channels),
    });
    setOtherChannelsOpen(false);
  }, [data]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const lockDate = (k) => mode === "edit" && !!data?.[k];
  const basicDateBlank = !form.basic_details_filled_date;
  const allDateBlank = !form.all_details_filled_date;

  function redMissing(fieldKey) {
    return basicDateBlank && BASIC_FIELDS.includes(fieldKey) && !filled(form[fieldKey]);
  }

  function orangeMissing(fieldKey) {
    return (
      allDateBlank &&
      ALL_FIELDS.includes(fieldKey) &&
      !BASIC_FIELDS.includes(fieldKey) &&
      !filled(form[fieldKey])
    );
  }

  function fieldInputStyle(fieldKey, readOnly = false) {
    let base = readOnly ? { ...s.readOnly } : { ...s.input };
    if (redMissing(fieldKey)) base = { ...base, ...s.redMissing };
    if (orangeMissing(fieldKey)) base = { ...base, ...s.orangeMissing };
    return base;
  }

  function fieldAreaStyle(fieldKey) {
    let base = { ...s.ta };
    if (redMissing(fieldKey)) base = { ...base, ...s.redMissing };
    if (orangeMissing(fieldKey)) base = { ...base, ...s.orangeMissing };
    return base;
  }

  function buildPayload() {
    const p = { ...form, product_category: form.product_category || null };
    p.i_sku = p.i_sku || generateISku(p.product_category, categories);
    p.primary_channel = String(p.primary_channel || "").trim();
    p.other_channels = joinChannelValues((p.other_channels || []).filter((x) => x && x !== p.primary_channel));
    if (p.i_sku_type === "single" && !hasTableData(p.bundle_data)) p.bundle_data = { remark: "no-data-entered" };
    if (!p.basic_details_filled_date && allFilled(p, BASIC_FIELDS)) p.basic_details_filled_date = isoToday();
    if (!p.all_details_filled_date && allFilled(p, ALL_FIELDS)) p.all_details_filled_date = isoToday();
    return pickApiFields(p);
  }

  function save() {
    if (!form.product_name || !form.product_category || !form.about_product) return alert("Product name, category and about product are required");
    const payload = buildPayload();
    if (!payload.i_sku) return alert("Unable to generate I-SKU");
    onSave(payload);
  }

  const selectedOtherChannels = splitChannelValues(form.other_channels).filter((value) => value !== form.primary_channel);
  const availableOtherChannelOptions = marketplaceOptions.filter(
    (option) => option.v !== form.primary_channel && !selectedOtherChannels.includes(option.v),
  );
  const channelLabelByValue = useMemo(
    () => Object.fromEntries(marketplaceOptions.map((option) => [option.v, option.l])),
    [marketplaceOptions],
  );

  function addOtherChannel(value) {
    const nextValue = String(value || "").trim();
    if (!nextValue || nextValue === form.primary_channel || selectedOtherChannels.includes(nextValue)) return;
    set("other_channels", [...selectedOtherChannels, nextValue]);
  }

  function removeOtherChannel(valueToRemove) {
    set("other_channels", selectedOtherChannels.filter((value) => value !== valueToRemove));
  }

  return (
    <div style={s.modal}>
      <div style={s.modalHead}>
        <div><strong>{mode === "new" ? "New NPD" : "Edit NPD"}</strong><div style={s.small}>{form.i_sku || "I-SKU auto-generated"}</div></div>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={s.btnGhost} onClick={onClose}>Cancel</button>
          <button style={s.btn} onClick={save}>Save</button>
          <button style={s.btnClose} onClick={onClose}>x</button>
        </div>
      </div>
      <div style={s.modalBody}>
        <SectionTitle title="Core Info" />
        <div style={s.grid2}>
          <Field label="I-SKU (auto)"><input style={fieldInputStyle("i_sku", true)} value={form.i_sku || generateISku(form.product_category, categories)} readOnly /></Field>
          <Field label="Product Name *"><input style={fieldInputStyle("product_name")} value={form.product_name} onChange={(e) => set("product_name", e.target.value)} /></Field>
          <Field label="Final NPD Status"><select style={fieldInputStyle("final_npd_status")} value={form.final_npd_status} onChange={(e) => set("final_npd_status", e.target.value)}>{NPD_STATUS_OPTIONS.map((v) => <option key={v} value={v}>{toLabel(v)}</option>)}</select></Field>
          <Field label="I-SKU Type"><select style={fieldInputStyle("i_sku_type")} value={form.i_sku_type} onChange={(e) => set("i_sku_type", e.target.value)}><option value="single">Single</option><option value="bundle">Bundle</option><option value="variant_set">Variant Set</option></select></Field>
          <Field label="Date of Ideation"><input type="date" style={fieldInputStyle("date_of_ideation", lockDate("date_of_ideation"))} disabled={lockDate("date_of_ideation")} value={form.date_of_ideation || ""} onChange={(e) => set("date_of_ideation", e.target.value)} /></Field>
          <Field label="Category *"><select style={fieldInputStyle("product_category")} value={form.product_category} onChange={(e) => set("product_category", e.target.value)}><option value="">Select</option>{categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}</select></Field>
          <Field label="Ideation Closure Date"><input type="date" style={fieldInputStyle("icd_ideation_closure_date", lockDate("icd_ideation_closure_date"))} disabled={lockDate("icd_ideation_closure_date")} value={form.icd_ideation_closure_date || ""} onChange={(e) => set("icd_ideation_closure_date", e.target.value)} /></Field>
          
        </div>
        <Field label="About Product *"><textarea style={fieldAreaStyle("about_product")} value={form.about_product} onChange={(e) => set("about_product", e.target.value)} /></Field>
        <Field label="USP"><textarea style={fieldAreaStyle("usp")} value={form.usp} onChange={(e) => set("usp", e.target.value)} /></Field>
        <Field label="Key Highlight Points"><textarea style={fieldAreaStyle("key_highlight_points")} value={form.key_highlight_points} onChange={(e) => set("key_highlight_points", e.target.value)} /></Field>
        <Field label="Application"><textarea style={fieldAreaStyle("application")} value={form.application} onChange={(e) => set("application", e.target.value)} /></Field>

        <SectionTitle title="Commercial" />
        <div style={s.grid3}>
          <Field label="Target Cost (INR)"><input type="number" style={fieldInputStyle("target_cost")} value={form.target_cost || ""} onChange={(e) => set("target_cost", e.target.value)} /></Field>
          <Field label="Expected SP (INR)"><input type="number" style={fieldInputStyle("expected_sp")} value={form.expected_sp || ""} onChange={(e) => set("expected_sp", e.target.value)} /></Field>
          
        </div>
        <Field label="Target Cost Breakup"><textarea style={fieldAreaStyle("target_cost_breakup")} value={form.target_cost_breakup || ""} onChange={(e) => set("target_cost_breakup", e.target.value)} /></Field>

        <SectionTitle title="Physical & Compliance" />
        <div style={s.grid2}>
          <Field label="HSN"><input style={fieldInputStyle("hsn")} value={form.hsn || ""} onChange={(e) => set("hsn", String(e.target.value || "").replace(/\D/g, ""))} placeholder="Minimum 6 digits" /></Field>
          <Field label="GST %">
            <select
              style={fieldInputStyle("gst_percent")}
              value={String(form.gst_percent || "")}
              onChange={(e) => set("gst_percent", e.target.value)}
            >
              <option value="">Select GST %</option>
              {GST_PERCENT_OPTIONS.map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </Field>
        </div>

        {form.i_sku_type === "bundle" && <Field label="Bundle SKU Breakup"><TableEditor fixedColumns={BUNDLE_FIXED} value={form.bundle_data} onChange={(v) => set("bundle_data", v)} highlight={redMissing("bundle_data") ? "red" : orangeMissing("bundle_data") ? "orange" : ""} /></Field>}
        <Field label="Variation Breakup"><TableEditor fixedColumns={VARIATION_FIXED} value={form.variation_data} onChange={(v) => set("variation_data", v)} highlight={redMissing("variation_data") ? "red" : orangeMissing("variation_data") ? "orange" : ""} /></Field>

        <SectionTitle title="Channels & Vendor" />
        <div style={s.grid2}>
          <Field label="Primary Channel">
            <select
              style={fieldInputStyle("primary_channel")}
              value={form.primary_channel || ""}
              onChange={(e) => {
                const nextPrimary = e.target.value;
                const nextOther = splitChannelValues(form.other_channels).filter((value) => value !== nextPrimary);
                setForm((prev) => ({ ...prev, primary_channel: nextPrimary, other_channels: nextOther }));
              }}
            >
              <option value="">Select primary marketplace</option>
              {marketplaceOptions.map((option) => <option key={option.v} value={option.v}>{option.l}</option>)}
            </select>
          </Field>
          <Field label="Other Channels">
            <div
              style={{ ...fieldInputStyle("other_channels"), ...s.multiSelectShell }}
              onClick={() => setOtherChannelsOpen(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setOtherChannelsOpen(true);
                }
              }}
            >
              <div style={s.multiSelectChips}>
                {selectedOtherChannels.length === 0 && (
                  <span style={s.multiSelectPlaceholder}>Click to select marketplaces</span>
                )}
                {selectedOtherChannels.map((value) => (
                  <span key={value} style={s.marketChip}>
                    <span>{channelLabelByValue[value] || value}</span>
                    <button
                      type="button"
                      style={s.marketChipRemove}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeOtherChannel(value);
                      }}
                      aria-label={`Remove ${channelLabelByValue[value] || value}`}
                    >
                      x
                    </button>
                  </span>
                ))}
              </div>
              <button
                type="button"
                style={s.multiSelectToggle}
                onClick={(e) => {
                  e.stopPropagation();
                  setOtherChannelsOpen((prev) => !prev);
                }}
              >
                {otherChannelsOpen ? "Hide" : "Select"}
              </button>
            </div>
            {otherChannelsOpen && (
              <select
                style={{ ...s.input, marginTop: 6 }}
                value=""
                onChange={(e) => {
                  addOtherChannel(e.target.value);
                }}
              >
                <option value="" disabled>
                  {availableOtherChannelOptions.length ? "Select marketplace" : "No more marketplaces"}
                </option>
                {availableOtherChannelOptions.map((option) => (
                  <option key={option.v} value={option.v}>
                    {option.l}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label="Catalogue Link"><input style={fieldInputStyle("catalogue_link")} value={form.catalogue_link || ""} onChange={(e) => set("catalogue_link", e.target.value)} /></Field>
          <Field label="Technical Specifications"><textarea style={fieldAreaStyle("technical_specifications")} value={form.technical_specifications || ""} onChange={(e) => set("technical_specifications", e.target.value)} /></Field>
        </div>

        <SectionTitle title="Stage Tracking" />
        <div style={s.stageGrid}>
          {STAGES.map((st) => (
            <div key={st.key} style={s.stageCard}>
              <div style={s.stageName}>{st.label}</div>
              <Field label="Status">
                {(() => {
                  const isInbound = st.key === "inbound";
                  const statusKey = `${st.key}_status`;
                  return (
                <select
                  style={isInbound ? s.input : s.readOnly}
                  disabled={!isInbound}
                  value={form[statusKey] || "not_started"}
                  onChange={(e) => set(statusKey, e.target.value)}
                >
                  {STAGE_STATUS_OPTIONS.map((v) => <option key={v} value={v}>{toLabel(v)}</option>)}
                </select>
                  );
                })()}
              </Field>
              <Field label="Target Date">
                {(() => {
                  const targetKey = `${st.key}_target_date`;
                  return (
                <input
                  type="date"
                  style={fieldInputStyle(targetKey, lockDate(targetKey))}
                  disabled={lockDate(targetKey)}
                  value={form[targetKey] || ""}
                  onChange={(e) => set(targetKey, e.target.value)}
                />
                  );
                })()}
              </Field>
              <Field label="Completion Date">
                {(() => {
                  const isInbound = st.key === "inbound";
                  const completionKey = `${st.key}_completion_date`;
                  return (
                <input
                  type="date"
                  style={fieldInputStyle(completionKey, !isInbound)}
                  disabled={!isInbound}
                  value={form[completionKey] || ""}
                  onChange={(e) => set(completionKey, e.target.value)}
                />
                  );
                })()}
              </Field>
            </div>
          ))}
        </div>

        <SectionTitle title="Overall & Remarks" />
        <div style={s.grid3}>
          <Field label="Final NPD Status"><select style={fieldInputStyle("final_npd_status")} value={form.final_npd_status} onChange={(e) => set("final_npd_status", e.target.value)}>{NPD_STATUS_OPTIONS.map((v) => <option key={v} value={v}>{toLabel(v)}</option>)}</select></Field>
          <Field label="Manual Tag"><input style={s.input} value={form.manual_tag || ""} onChange={(e) => set("manual_tag", e.target.value)} placeholder="e.g. Q2 Launch" /></Field>
          <div />
        </div>
        <Field label="Remarks"><textarea style={s.ta} value={form.remarks || ""} onChange={(e) => set("remarks", e.target.value)} /></Field>
      </div>
    </div>
  );
}

function TableEditor({ fixedColumns, value, onChange, highlight = "" }) {
  const table = initTable(value, fixedColumns);
  const cols = table.columns || fixedColumns.map((c) => c.key);
  const meta = cols.map((k) => {
    const f = fixedColumns.find((c) => c.key === k);
    return { key: k, label: f?.label || k.replaceAll("_", " "), type: f?.type || "text" };
  });

  function setTable(next) { onChange({ ...next, columns: next.columns || cols, remark: undefined }); }
  function addRow() {
    const cleanRows = Array.isArray(table.rows) ? table.rows : [];
    const newRow = Object.fromEntries(cols.map((c) => [c, ""]));
    setTable({ ...table, rows: [...cleanRows, newRow] });
  }
  function delRow(i) { setTable({ ...table, rows: (table.rows || []).filter((_, idx) => idx !== i) }); }
  function setCell(i, k, v) { const rows = [...(table.rows || [])]; rows[i] = { ...(rows[i] || {}), [k]: v }; setTable({ ...table, rows }); }
  function addCol() {
    const name = window.prompt("New column name");
    if (!name) return;
    const key = name.toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
    if (!key || cols.includes(key)) return;
    const nextCols = [...cols, key];
    const rows = (table.rows || []).map((r) => ({ ...r, [key]: "" }));
    setTable({ ...table, columns: nextCols, rows });
  }

  return (
    <div
      style={{
        ...s.tblWrap,
        ...(highlight === "red" ? s.redMissing : {}),
        ...(highlight === "orange" ? s.orangeMissing : {}),
      }}
    >
      <div style={{ marginBottom: 8 }}>
        <button style={s.btnGhost} type="button" onClick={addRow}>+ Add Row</button>
        {" "}
        <button style={s.btnGhost} type="button" onClick={addCol}>+ Add Column</button>
      </div>
      <div style={s.tblScroll}>
        <table style={s.tbl}>
          <thead><tr>{meta.map((m) => <th key={m.key} style={s.th}>{m.label}</th>)}<th style={s.th}>Action</th></tr></thead>
          <tbody>
            {(table.rows || []).map((row, i) => (
              <tr key={i}>
                {meta.map((m) => <td key={m.key} style={s.td}><input type={m.type} style={s.cell} value={row[m.key] ?? ""} onChange={(e) => setCell(i, m.key, e.target.value)} /></td>)}
                <td style={s.td}><button style={s.btnDanger} type="button" onClick={() => delRow(i)}>Remove</button></td>
              </tr>
            ))}
            {(table.rows || []).length === 0 && <tr><td colSpan={meta.length + 1} style={s.empty}>No rows</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <div style={{ marginBottom: 10 }}><label style={s.label}>{label}</label>{children}</div>;
}

function SectionTitle({ title }) {
  return <div style={s.sectionTitle}>{title}</div>;
}

const s = {
  page: { padding: 24, maxWidth: 1200, margin: "0 auto", fontFamily: "Segoe UI, Arial" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 },
  title: { margin: 0, color: "#111827" },
  sub: { margin: "4px 0 0", color: "#6b7280", fontSize: 12 },
  err: { color: "#b91c1c", marginBottom: 8, fontSize: 12 },
  refreshInfo: {
    marginBottom: 10,
    border: "1px solid #d1fae5",
    background: "#ecfdf5",
    color: "#065f46",
    borderRadius: 10,
    padding: "8px 10px",
    fontSize: 12,
  },
  refreshGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))",
    gap: 6,
  },
  btn: { border: "none", borderRadius: 8, padding: "9px 12px", background: "#111827", color: "#fff", cursor: "pointer" },
  btnGhost: { border: "1px solid #d1d5db", borderRadius: 8, padding: "7px 10px", background: "#fff", cursor: "pointer", color: "#111827" },
  btnDanger: { border: "none", background: "transparent", color: "#b91c1c", cursor: "pointer" },
  btnClose: { border: "1px solid #d1d5db", borderRadius: 8, padding: "7px 10px", background: "#fff", cursor: "pointer", color: "#111827", fontWeight: 700 },
  filters: { display: "flex", gap: 8, marginBottom: 10 },
  input: { width: "100%", border: "1px solid #d1d5db", borderRadius: 8, padding: "8px 10px", boxSizing: "border-box", background: "#fff", color: "#111827" },
  readOnly: { width: "100%", border: "1px solid #d1d5db", borderRadius: 8, padding: "8px 10px", background: "#f3f4f6", color: "#6b7280", boxSizing: "border-box" },
  ta: { width: "100%", border: "1px solid #d1d5db", borderRadius: 8, padding: "8px 10px", minHeight: 56, boxSizing: "border-box", resize: "vertical", color: "#111827", background: "#fff" },
  multiSelectShell: { minHeight: 42, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, cursor: "pointer" },
  multiSelectChips: { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", minHeight: 24, flex: 1 },
  multiSelectPlaceholder: { fontSize: 13, color: "#6b7280" },
  marketChip: { display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 8px", borderRadius: 999, background: "#eef2ff", color: "#1e3a8a", fontSize: 12, fontWeight: 600 },
  marketChipRemove: { border: "none", background: "transparent", color: "#1e3a8a", cursor: "pointer", padding: 0, lineHeight: 1, fontSize: 12, fontWeight: 700 },
  multiSelectToggle: { border: "1px solid #d1d5db", borderRadius: 6, background: "#fff", color: "#111827", fontSize: 12, padding: "4px 8px", cursor: "pointer", whiteSpace: "nowrap" },
  list: { border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden" },
  actionRow: { display: "flex", gap: 8, alignItems: "center" },
  outerTable: { width: "100%", minWidth: 1500, borderCollapse: "collapse", background: "#fff" },
  outerTh: {
    textAlign: "left",
    padding: "10px 12px",
    background: "#f9fafb",
    borderBottom: "1px solid #e5e7eb",
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: "#6b7280",
    whiteSpace: "nowrap",
  },
  outerTr: { borderBottom: "1px solid #f3f4f6", cursor: "pointer" },
  outerTd: { padding: "10px 12px", fontSize: 13, color: "#111827", verticalAlign: "middle", whiteSpace: "nowrap" },
  outerTdEmpty: { padding: 16, textAlign: "center", color: "#6b7280" },
  rowHead: { display: "grid", gridTemplateColumns: "200px 1.4fr 1fr 140px", gap: 8, padding: "10px 12px", background: "#f9fafb", fontSize: 11, textTransform: "uppercase", color: "#6b7280", letterSpacing: "0.06em", alignItems: "center" },
  row: { display: "grid", gridTemplateColumns: "200px 1.4fr 1fr 140px", gap: 8, padding: "12px", borderTop: '1px solid #f3f4f6', background: "#fff", cursor: "pointer", textAlign: "left", color: "#111827", border: "none", alignItems: "center" },
  rowOuterhead: {
  display: "grid",
  gridTemplateColumns: "150px 1fr 150px 120px", // adjust widths to match your data
  gap: 12,
  padding: "8px 16px",
  background: "#f9fafb",
  borderBottom: "2px solid #e5e7eb",
  fontSize: 11,
  fontWeight: 600,
  color: "#6b7280",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  },
  rowOuter: {
  display: "grid",
  gridTemplateColumns: "150px 1fr 150px 120px", // must match rowHead exactly
  gap: 12,
  padding: "10px 16px",
  width: "100%",
  textAlign: "left",      // ← this is usually the culprit on <button>
  alignItems: "center",
  background: "#fff",
  border: "none",
  borderBottom: "1px solid #f3f4f6",
  cursor: "pointer",
  fontSize: 13,
  color: "#111827",
  fontFamily: "inherit",
  },
  
  empty: { padding: 16, textAlign: "center", color: "#6b7280" },
  overlay: { position: "fixed", inset: 0, background: "rgba(15,23,42,0.35)", zIndex: 1000 },
  modal: { position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: "min(1200px,96vw)", height: "min(90vh,900px)", background: "#fff", border: "1px solid #d1d5db", borderRadius: 12, zIndex: 1001, display: "flex", flexDirection: "column" },
  summaryModal: { position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: "min(1000px,94vw)", height: "min(80vh,760px)", background: "#fff", border: "1px solid #d1d5db", borderRadius: 12, zIndex: 1001, display: "flex", flexDirection: "column" },
  modalHead: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: 12, borderBottom: "1px solid #e5e7eb", background: "#f8fafc" },
  small: { fontSize: 12, color: "#6b7280", marginTop: 2 },
  modalBody: { padding: 12, overflowY: "auto" },
  summaryBody: { padding: 12, overflow: "auto", height: "100%" },
  summaryLoading: { display: "flex", alignItems: "center", gap: 10, color: "#374151", fontSize: 14, padding: 8 },
  loaderDot: { width: 14, height: 14, borderRadius: "50%", border: "2px solid #c7d2fe", borderTopColor: "#4f46e5", animation: "spin 0.9s linear infinite" },
  summaryPre: { margin: 0, fontSize: 12, lineHeight: 1.45, background: "#0b1020", color: "#e2e8f0", padding: 12, borderRadius: 8, overflow: "auto", minHeight: 120 },
  label: { display: "block", fontSize: 11, color: "#374151", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 },
  sectionTitle: { margin: "12px 0 8px", fontSize: 13, fontWeight: 700, color: "#111827", textTransform: "uppercase", letterSpacing: "0.04em" },
  grid2: { display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 10 },
  grid3: { display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 10 },
  redDate: { borderColor: "#dc2626", background: "#fef2f2" },
  orangeDate: { borderColor: "#ea580c", background: "#fff7ed" },
  redMissing: { borderColor: "#dc2626", boxShadow: "0 0 0 1px #dc2626 inset" },
  orangeMissing: { borderColor: "#ea580c", boxShadow: "0 0 0 1px #ea580c inset" },
  tblWrap: { border: "1px solid #e5e7eb", borderRadius: 10, padding: 8 },
  tblScroll: { overflowX: "auto" },
  tbl: { width: "100%", minWidth: 760, borderCollapse: "collapse" },
  th: { textAlign: "left", padding: "7px 8px", borderBottom: "1px solid #e5e7eb", fontSize: 11, color: "#6b7280" },
  td: { padding: "7px 8px", borderBottom: "1px solid #f3f4f6" },
  cell: { width: "100%", border: "1px solid #d1d5db", borderRadius: 6, padding: "6px 8px", boxSizing: "border-box", color: "#111827", background: "#fff" },


  btnLink:          { background: "none", border: "none", color: "#999", fontSize: 12, cursor: "pointer", padding: 0, marginTop: 4 },
  addComponentBtn:  { width: "100%", border: "1px dashed #ddd", borderRadius: 8, padding: "9px", fontSize: 13, color: "#aaa", cursor: "pointer", background: "none", marginTop: 4 },

  // Stages
  stageGrid: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 },
  stageCard: { border: "1px solid #e5e7eb", borderRadius: 8, padding: "12px 14px", background: "#fff" },
  stageName: { fontSize: 12, fontWeight: 700, marginBottom: 10, color: "#111827" },
};
